import type { IScope } from "angular";
import { finalize } from "rxjs/operators";

import { utc } from "moment-timezone";

import { cloneDeep, isEmpty } from "lodash";

import { DefaultActions } from "App/Modules/Gui/Components/ActionMenu/DefaultActions";
import type { IAction } from "App/Modules/Gui/Components/ActionMenu/IAction";
import type { IConfigurationGroupRecording, IResponseWithLoading, ISite } from "App/Modules/Models";
import { DayType, DayTypeFunctions } from "App/Modules/Models";
import type { ISitesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class ConfigurationGroupRecordingComponentController {
    public static $inject: string[] = ["$scope", "mmSitesRepository"];

    public original: IConfigurationGroupRecording;

    public modified: IConfigurationGroupRecording;

    public actions: IAction[];

    public dayTypes: Array<{ type: DayType; name: string }>;

    public editResponse: IResponseWithLoading<IConfigurationGroupRecording>;

    public deleteResponse: IResponseWithLoading<void>;

    public site: IResponseWithLoading<ISite>;

    public resolve: IFormContainerResolve<IConfigurationGroupRecording, void>;

    constructor(private readonly $scope: IScope, private readonly sitesRepository: ISitesRepository) {}

    public $onInit() {
        this.original = this.resolve.formData;
        this.modified = cloneDeep(this.original);

        this.actions = [
            {
                ...DefaultActions.DeleteAction,
                click: this.delete.bind(this),
                isDisabled: () =>
                    this.original.functionalityRecordings.length > 0 ||
                    (this.editResponse && this.editResponse.isLoading),
                isTooltipHidden: () => this.original.functionalityRecordings.length <= 0,
                response: () => this.deleteResponse,
                tooltip: () => "Gui.Manage.RemoveConfigurationGroupTooltip",
            },
        ];

        this.dayTypes = DayTypeFunctions.WeekDays().map((dt) => ({
            type: dt,
            name: DayType[dt],
        }));

        extendWithLoadingAndFail(this.sitesRepository.getSite(this.original.siteId))
            .pipe(
                finalize(() => {
                    this.$scope.$applyAsync();
                }),
            )
            .subscribe(
                (result) => {
                    this.site = result;
                },
                (result) => {
                    this.site = result;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public save() {
        const delta = PatchDelta.difference(this.original, this.modified);

        if (delta.schedule && delta.schedule.scheduleExceptions) {
            delta.schedule.scheduleExceptions = delta.schedule.scheduleExceptions.filter((exception) => {
                return exception.dtStop.isAfter(utc().tz(this.site.data.timeZone));
            });
        }

        if (!isEmpty(delta)) {
            extendWithLoadingAndFail(
                this.sitesRepository.patchConfigurationGroupRecording(this.original.siteId, this.original.id, delta),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<IConfigurationGroupRecording>) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.resolve.options.onCancel();
        }
    }

    public delete() {
        extendWithLoadingAndFail(this.sitesRepository.deleteConfigurationGroup(this.original.siteId, this.original.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse: IResponseWithLoading<void>) => {
                    this.deleteResponse = deleteResponse;

                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                    }
                },
                (error) => {
                    this.deleteResponse = error;
                },
            );
    }
}
